import React, { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { FaInstagram, FaFacebookF, FaTiktok } from "react-icons/fa";
import { useNavigate, useLocation } from "react-router-dom";

export default function Navbar() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState("");
  const navigate = useNavigate();
  const location = useLocation();

  const menuItemsLeft = [
    { href: "/#hero", label: "HOME", id: "hero" },
    { href: "/#o-mnie", label: "O MNIE", id: "o-mnie" },
    { href: "/#galeria", label: "GALERIA", id: "galeria" },
    { href: "/#opinie", label: "OPINIE", id: "opinie" },
  ];

  const menuItemsRight = [
    { href: "/oferta", label: "OFERTA" },
    { href: "/pytania", label: "PYTANIA" },
    { href: "/kontakt", label: "KONTAKT" },
    {
      href: "/#kontakt",
      label: "FORMULARZ",
      id: "kontakt",
    },
  ];

  useEffect(() => {
    const handleScroll = () => {
      const sections = document.querySelectorAll("section[id]");
      let current = "";
      sections.forEach((section) => {
        const rect = section.getBoundingClientRect();
        if (rect.top <= 80 && rect.bottom >= 80) {
          current = section.id;
        }
      });
      setActiveSection(current);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const getLinkClass = (id) =>
    `hover:text-orange-400 transition px-2 py-1 ${
      activeSection === id ? "border-b-2 border-orange-500" : ""
    }`;

  const handleNavigate = (href) => {
    setMenuOpen(false);

    // Scroll to anchors on main page
    if (href.startsWith("/#")) {
      navigate("/");
      setTimeout(() => {
        const el = document.getElementById(href.split("#")[1]);
        if (el) {
          el.scrollIntoView({ behavior: "smooth" });
        } else {
          window.scrollTo({ top: 0, behavior: "smooth" });
        }
      }, 50);
    } else if (href === "/#kontakt") {
      navigate("/");
      setTimeout(() => {
        const el = document.getElementById("kontakt");
        if (el) {
          el.scrollIntoView({ behavior: "smooth" });
        } else {
          window.scrollTo({ top: 0, behavior: "smooth" });
        }
      }, 50);
    } else if (href.startsWith("#")) {
      const el = document.getElementById(href.slice(1));
      if (el) {
        el.scrollIntoView({ behavior: "smooth" });
      } else {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    } else if (["/oferta", "/kontakt", "/pytania"].includes(href)) {
      navigate(href);
      setTimeout(() => {
        const hero = document.getElementById("hero");
        if (hero) {
          hero.scrollIntoView({ behavior: "smooth" });
        } else {
          window.scrollTo({ top: 0, behavior: "smooth" });
        }
      }, 50);
    } else {
      navigate(href);
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }, 50);
    }
  };

  return (
    <header className="bg-black/40 text-white fixed w-full z-50 shadow-md backdrop-blur-sm font-poppins font-normal">
      <div className="text-sm px-6 py-2 max-w-screen-2xl mx-auto flex flex-col md:flex-row md:justify-between md:items-center gap-2 text-center">
        <div className="hidden md:flex space-x-4 justify-center">
          <a
            href="https://www.instagram.com/dj_kusy/"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Instagram"
            className="hover:text-orange-500"
          >
            <FaInstagram size={18} />
          </a>
          <a
            href="https://www.facebook.com/djkusy/"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Facebook"
            className="hover:text-orange-500"
          >
            <FaFacebookF size={18} />
          </a>
          <a
            href="https://www.tiktok.com/@dj_kusy"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="TikTok"
            className="hover:text-orange-500"
          >
            <FaTiktok size={18} />
          </a>
        </div>
        <div className="flex justify-center gap-4 md:justify-end text-sm">
          <a href="tel:+48690442187" className="hover:text-orange-500">
            +48 690 442 187
          </a>
          <a href="mailto:biuro@djkusy.pl" className="hover:text-orange-500">
            biuro@djkusy.pl
          </a>
        </div>
      </div>

      <div className="flex items-center justify-between px-6 py-4 max-w-screen-2xl mx-auto relative">
        <nav className="hidden md:flex space-x-8 text-base uppercase">
          {menuItemsLeft.map(({ href, label, id }) => (
            <button
              key={href}
              onClick={() => handleNavigate(href)}
              className={getLinkClass(id)}
            >
              {label}
            </button>
          ))}
        </nav>

        <div className="flex-shrink-0 absolute left-1/2 transform -translate-x-1/2">
          <button
            onClick={() => handleNavigate("/#hero")}
            aria-label="Strona główna"
          >
            <img
              src="/assets/logo/kusy-logo.webp"
              alt="Logo DJ Kusy"
              fetchpriority="high"
              loading="eager"
              className="h-14 md:h-16 w-auto mx-auto"
            />
          </button>
        </div>

        <nav className="hidden md:flex space-x-8 text-base uppercase">
          {menuItemsRight.map(({ href, label, id }) => (
            <button
              key={href}
              onClick={() => handleNavigate(href)}
              className={getLinkClass(id)}
            >
              {label}
            </button>
          ))}
        </nav>

        <button
          className="md:hidden z-50"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label={menuOpen ? "Zamknij menu" : "Otwórz menu"}
        >
          {menuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {menuOpen && (
        <div className="md:hidden w-full bg-black bg-opacity-95 text-white flex flex-col items-center px-6 py-6 space-y-4 text-base uppercase tracking-wider z-40">
          {[...menuItemsLeft, ...menuItemsRight].map(({ href, label, id }) => (
            <button
              key={href}
              onClick={() => handleNavigate(href)}
              className={`w-full text-center py-2 ${getLinkClass(id)}`}
            >
              {label}
            </button>
          ))}
        </div>
      )}
    </header>
  );
}
